/**
 * NextImage.tsx
 *
 * A wrapper around Next.js <Image> with:
 * - Optional skeleton loader (pulse effect)
 * - Auto width styling unless overridden with Tailwind w- classes
 * - Blur placeholder class before load
 *
 * Fully compatible with:
 * - Node 20–24
 * - Next.js 13/14
 * - StaticImageData and string URLs
 */

import clsx from "clsx";
import Image, { ImageProps as NextImageProps } from "next/image";
import * as React from "react";

/**
 * Extended props for the wrapper component.
 *
 * `width` and `height` MUST be provided or Next.js <Image> will error.
 */
export type WrappedImageProps = {
  /** Adds a skeleton pulse while loading */
  useSkeleton?: boolean;

  /** Extra class for the underlying <img> */
  imgClassName?: string;

  /** Class applied when in blur/skeleton state */
  blurClassName?: string;

  /** Required alt text */
  alt: string;

  /** Explicit width (number or string allowed for consistency) */
  width: number | string;

  /** Explicit height (number or string allowed for consistency) */
  height: number | string;
} & Omit<NextImageProps, "width" | "height" | "alt">;

export default function NextImage({
  useSkeleton = false,
  src,
  width,
  height,
  alt,
  className,
  imgClassName,
  blurClassName,
  ...rest
}: WrappedImageProps) {
  /**
   * Image load status:
   * - "loading" = apply blur/skeleton
   * - "complete" = show final image
   */
  const [status, setStatus] = React.useState<
    "loading" | "complete"
  >(useSkeleton ? "loading" : "complete");

  /**
   * If a Tailwind width class (w-*) is present,
   * do NOT apply inline width styling.
   */
  const widthIsSet =
    typeof className === "string" ? className.includes("w-") : false;

  return (
    <figure
      style={
        !widthIsSet && typeof width === "number"
          ? { width: `${width}px` }
          : undefined
      }
      className={className}
    >
      <Image
        {...rest}
        src={src}
        alt={alt}
        width={typeof width === "string" ? parseInt(width, 10) : width}
        height={typeof height === "string" ? parseInt(height, 10) : height}
        onLoadingComplete={() => setStatus("complete")}
        className={clsx(
          imgClassName,
          // Hide alt text before load
          "bg-gray-400 text-gray-400",
          status === "loading" && clsx("animate-pulse", blurClassName)
        )}
        /**
         * layout="responsive" is deprecated in Next.js 13/14.
         *
         * Instead, width + height automatically make the image responsive.
         * If a responsive behavior is needed, wrap the image in a ratio‑controlled container.
         */
        style={{
          height: "auto",
        }}
      />
    </figure>
  );
}
